import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import getCustomerData from "./getCustomerData";
import getCustomerMergeTarget from "./getCustomerMergeTarget";
import { Customer } from "./types";
import useTranslations from "./useTranslations";
import getAppointmentCustomerData from "./getAppointmentCustomerData";

const useCustomer = ({
  customerId,
  isOtherPerson,
}: {
  customerId?: string;
  isOtherPerson?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { translations } = useTranslations();
  const { defaultError, errorCustomerNotFound } = translations;

  const [data, setData] = useState<Customer | null>(null);

  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const updateCustomer = useCallback(async () => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      let customer: Customer | null = null;
      try {
        if (isOtherPerson) {
          customer = await getAppointmentCustomerData(customerId);
        }

        if (!isOtherPerson) {
          customer = await getCustomerData(customerId);
        }
      } catch (err) {
        throw new Error(defaultError);
      }

      if (!customer) {
        throw new Error(errorCustomerNotFound);
      }

      // Direct link to an already-merged customer — redirect to whoever it
      // was (possibly transitively) merged into instead of showing the dead
      // record. Only for the "this IS the customer being viewed" case, not
      // the isOtherPerson (appointment-context) embedded view.
      if (!isOtherPerson && customer.merge_status === "merged") {
        const targetId = await getCustomerMergeTarget(customer.id);
        if (targetId) {
          navigate(location.pathname.replace(customer.id, targetId), {
            replace: true,
          });
          return;
        }
      }

      setData(customer);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [
    defaultError,
    errorCustomerNotFound,
    customerId,
    isOtherPerson,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    updateCustomer();
  }, [updateCustomer]);

  return { isLoading, error, data, refetch: updateCustomer };
};

export default useCustomer;
